import { type ReactNode } from 'react';
import clsx from 'clsx';
import { Heading, Paragraph } from '@hungryroot/ui-kit';
import {
  ProductInfo,
  type ProductInfoLabelValue,
} from '../ProductInfo/ProductInfo';
import { ShowMore } from '../ShowMore/ShowMore';
import styles from './Nutrition.module.css';

enum IndentLevel {
  Zero = 0,
  One = 1,
  Two = 2,
}

interface Section {
  label: string;
  firstSlotValue?: string;
  secondSlotValue?: string;
  className?: string;
  subSections?: Section[];
}

interface NutritionProps {
  productInfo: ProductInfoLabelValue[];
  indentLevel?: IndentLevel;
  servings: ReactNode;
  sections: Section[];
  disclaimer: string;
  editWarning?: string;
  className?: string;
  expandedContentClassName?: string;
  onShowMoreNutrition?: (isExpanded: boolean) => void;
  initialIsExpanded?: boolean;
}

export function Nutrition(props: NutritionProps) {
  const {
    servings,
    productInfo,
    sections,
    disclaimer,
    editWarning,
    className,
    expandedContentClassName,
    onShowMoreNutrition,
    initialIsExpanded = false,
  } = props;

  const condensedContent = (
    <div className={className} data-testid="nutrition-info">
      <Heading className={styles.heading} variant="H5">
        Nutrition
      </Heading>
      {servings}
      <ProductInfo productInfo={productInfo} reverseLabelValue />
      {editWarning && (
        <Paragraph className={styles['edit-warning']}>{editWarning}</Paragraph>
      )}
    </div>
  );

  return (
    <ShowMore
      onShowMore={onShowMoreNutrition}
      className={`${styles['nutrition']} ${expandedContentClassName}`}
      condensedContent={condensedContent}
      expandedContent={
        <div>
          {condensedContent}
          <div className={styles['nutrition-table']}>
            {sections.map((section) => {
              return <NutritionRow key={section.label} section={section} />;
            })}
          </div>
          <Paragraph>{disclaimer}</Paragraph>
        </div>
      }
      initialIsExpanded={initialIsExpanded}
    />
  );
}

interface NutritionRowProps {
  section: Section;
  indentLevel?: IndentLevel;
}

function NutritionRow(props: NutritionRowProps) {
  const { section, indentLevel = IndentLevel.Zero } = props;

  return (
    <>
      <div className={`${styles.row} ${styles.divider} ${section.className}`}>
        {indentLevel === IndentLevel.Zero ? (
          <Heading variant="H6">{section.label}</Heading>
        ) : (
          <Paragraph
            className={clsx({
              [styles['indent-level-1']]: indentLevel === IndentLevel.One,
              [styles['indent-level-2']]: indentLevel === IndentLevel.Two,
            })}
          >
            {section.label}
          </Paragraph>
        )}
        <div className={styles.values}>
          <Paragraph className={styles['first-value']}>
            {section.firstSlotValue}
          </Paragraph>
          <Paragraph className={styles['second-value']}>
            {section.secondSlotValue}
          </Paragraph>
        </div>
      </div>
      {section?.subSections?.map((subSection) => {
        return (
          <NutritionRow
            key={subSection?.label}
            section={subSection}
            indentLevel={indentLevel + 1}
          />
        );
      })}
    </>
  );
}
